/**
 * ContactFormModal - Universal Contact Modal Component
 * Handles creation and editing of contacts (Phone/Email) for owners and merchants
 * 
 * Features:
 * - Support for PHONE and EMAIL types
 * - Real-time validation
 * - Primary contact tracking
 * - Can work with or without API (controlled/uncontrolled mode)
 * - Edit mode support
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import NotificationBanner from 'components/NotificationBanner';
import {
  filterContactPhone,
  validateContactPhone,
  validateContactEmail
} from 'modules/InputsValidations';

const ContactFormModal = ({ 
  isOpen = false,
  toggle = () => {},
  title = 'Add Contact',
  onSave = () => {},
  contactData = null, // For editing
  existingContacts = [], // To check for duplicate primary contacts
  loading = false,
  // API mode props (optional)
  apiEndpoint = null,
  apiService = null,
}) => {
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'PHONE',
    value: '',
    isPrimary: true
  });
  
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with editing data or reset
  useEffect(() => {
    if (isOpen) {
      if (contactData) {
        // Edit mode
        const isPrimaryValue = contactData.primary === 'Yes' || contactData.isPrimary === true;
        setFormData({
          type: contactData.type === 'Phone' ? 'PHONE' : contactData.type === 'Email' ? 'EMAIL' : contactData.type,
          value: contactData.value || '',
          isPrimary: isPrimaryValue,
          id: contactData.id
        });
      } else {
        // Create mode
        setFormData({
          type: 'PHONE',
          value: '',
          isPrimary: true
        });
      }
      setErrors({});
      setNotification({ show: false, type: 'success', message: '' });
    }
  }, [isOpen, contactData]);

  // Notification helpers
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    // Don't auto-hide error notifications, let user close them manually
    // Success notifications will be handled by the save function with setTimeout
    if (type === 'success') {
      setTimeout(() => {
        setNotification({ show: false, type: 'success', message: '' });
      }, 5000);
    }
    // Errors stay visible until user closes them or form is reset
  };

  const closeNotification = () => {
    setNotification({ show: false, type: 'success', message: '' });
  };

  // Handle input changes with validation
  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Apply filters
    if (field === 'value') {
      const currentType = field === 'type' ? value : formData.type;
      if (currentType === 'PHONE') {
        processedValue = filterContactPhone(value);
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Real-time validation
    if (field === 'isPrimary') {
      // Pass the new value and current type to validate
      validatePrimaryContact(processedValue, formData.type);
    } else if (field === 'type') {
      // When type changes, re-validate primary contact if it's set to true
      validateField(field, processedValue);
      // Re-validate primary after type change
      const newFormData = { ...formData, type: processedValue };
      if (newFormData.isPrimary) {
        validatePrimaryContact(newFormData.isPrimary, processedValue);
      }
    } else {
      validateField(field, processedValue);
    }
  };

  // Validate primary contact field
  const validatePrimaryContact = (isPrimary, contactType = null) => {
    setErrors(prev => {
      const copy = { ...prev };
      
      // If setting to false, remove the error immediately
      if (!isPrimary) {
        delete copy.primary;
        return copy;
      }
      
      // Use provided type or current formData type
      const typeToCheck = contactType || formData.type;
      
      // If setting to true, check for duplicates
      if (isPrimary && existingContacts && existingContacts.length > 0) {
        const hasPrimaryOfType = existingContacts.some((contact) => {
          // Skip if editing the same contact
          if (contactData && contact.id === contactData.id) {
            return false;
          }
          // Check if contact type matches and it's primary
          const contactType = contact.type === 'Phone' ? 'PHONE' : contact.type === 'Email' ? 'EMAIL' : contact.type;
          const contactIsPrimary = contact.primary === 'Yes' || contact.isPrimary === true;
          return contactType === typeToCheck && contactIsPrimary;
        });

        if (hasPrimaryOfType) {
          copy.primary = `A primary ${typeToCheck === 'PHONE' ? 'phone' : 'email'} contact already exists. Please uncheck primary or edit the existing one.`;
        } else {
          delete copy.primary;
        }
      } else {
        delete copy.primary;
      }
      
      return copy;
    });
  };

  // Validate individual field
  const validateField = (field, value) => {
    if (field === 'value' || field === 'type') {
      const type = field === 'type' ? value : formData.type;
      const val = field === 'type' ? formData.value : value;
      let result = { isValid: true, message: '' };
      
      // Only validate if we have a valid type and value
      if (type && (type === 'PHONE' || type === 'EMAIL') && val && val.trim() !== '') {
        if (type === 'PHONE') {
          result = validateContactPhone(val, 'Contact Phone');
        } else if (type === 'EMAIL') {
          result = validateContactEmail(val, 'Contact Email');
        }
      }
      
      if (result.isValid) {
        setErrors(prev => {
          const copy = { ...prev };
          delete copy.value;
          return copy;
        });
      } else {
        setErrors(prev => ({ ...prev, value: result.message }));
      }
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.type || (formData.type !== 'PHONE' && formData.type !== 'EMAIL')) {
      newErrors.type = 'Please select a Contact Type';
    }
    
    if (!formData.value || formData.value.trim() === '') {
      newErrors.value = 'Contact value is required';
    } else {
      if (formData.type === 'PHONE') {
        const result = validateContactPhone(formData.value, 'Contact Phone');
        if (!result.isValid) {
          newErrors.value = result.message;
        }
      } else if (formData.type === 'EMAIL') {
        const result = validateContactEmail(formData.value, 'Contact Email');
        if (!result.isValid) {
          newErrors.value = result.message;
        }
      }
    }

    // Check for duplicate primary contact
    if (formData.isPrimary && existingContacts && existingContacts.length > 0) {
      const hasPrimaryOfType = existingContacts.some((contact) => {
        // Skip if editing the same contact
        if (contactData && contact.id === contactData.id) {
          return false;
        }
        // Check if contact type matches and it's primary
        const contactType = contact.type === 'Phone' ? 'PHONE' : contact.type === 'Email' ? 'EMAIL' : contact.type;
        const contactIsPrimary = contact.primary === 'Yes' || contact.isPrimary === true;
        return contactType === formData.type && contactIsPrimary;
      });

      if (hasPrimaryOfType) {
        newErrors.primary = `A primary ${formData.type === 'PHONE' ? 'phone' : 'email'} contact already exists. Please uncheck primary or edit the existing one.`;
      }
    }
    
    return newErrors;
  };

  // Check if form is complete
  const isFormComplete = () => {
    if (!formData.type || (formData.type !== 'PHONE' && formData.type !== 'EMAIL')) return false;
    if (!formData.value || formData.value.trim() === '') return false;
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    // Final validation
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      showNotification('error', 'Please fix the errors in the form');
      return;
    }

    // If API mode is enabled
    if (apiEndpoint && apiService) {
      setIsSubmitting(true);
      try {
        const result = await apiService.post(apiEndpoint, formData);
        // Show success message and wait a bit before closing
        showNotification('success', `${isEditMode ? 'Contact updated' : 'Contact created'} successfully!`);
        
        // Wait 1.5 seconds to show success message before closing
        setTimeout(() => {
          onSave(result, contactData);
          toggle();
        }, 1500);
      } catch (error) {
        console.error('Error saving contact:', error);
        
        // Extract error message from response
        let errorMessage = 'An error occurred while saving the contact';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
        
        // Show detailed error message
        showNotification('error', `Error ${isEditMode ? 'updating' : 'creating'} contact: ${errorMessage}`);
        setIsSubmitting(false);
      }
    } else {
      // Callback mode
      showNotification('success', `${isEditMode ? 'Contact updated' : 'Contact created'} successfully!`);
      setTimeout(() => {
        onSave(formData, contactData);
        toggle();
      }, 1500);
    }
  };

  const isEditMode = !!contactData;
  const isBusy = loading || isSubmitting;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        <i className="ni ni-mobile-button mr-2"></i>
        {title}
      </ModalHeader>
      <ModalBody>
        {/* Notification Banner - Shows success/error messages */}
        {notification.show && (
          <NotificationBanner
            type={notification.type}
            message={notification.message}
            show={notification.show}
            onClose={closeNotification}
          />
        )}

        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="contactType">Contact Type *</Label>
              <Input
                id="contactType"
                type="select"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className={errors.type ? 'is-invalid' : ''}
              >
                <option value="PHONE">Phone</option>
                <option value="EMAIL">Email</option>
              </Input>
              {errors.type && (
                <div className="invalid-feedback d-block">{errors.type}</div>
              )}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="contactIsPrimary">Primary Contact</Label>
              <Input
                id="contactIsPrimary"
                type="select"
                value={formData.isPrimary.toString()}
                onChange={(e) => handleInputChange('isPrimary', e.target.value === 'true')}
                className={errors.primary ? 'is-invalid' : ''}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Input>
              {errors.primary && (
                <div className="invalid-feedback d-block">{errors.primary}</div>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="12">
            <FormGroup>
              <Label for="contactValue">Contact Value *</Label>
              <Input
                id="contactValue"
                type={formData.type === 'EMAIL' ? 'email' : 'tel'}
                value={formData.value}
                onChange={(e) => handleInputChange('value', e.target.value)}
                placeholder={formData.type === 'EMAIL' ? 'Enter email address' : 'Enter phone number'}
                required
                maxLength={formData.type === 'EMAIL' ? 255 : 14}
                className={errors.value ? 'is-invalid' : ''}
              />
              {errors.value && (
                <div className="invalid-feedback d-block">{errors.value}</div>
              )}
              {formData.type === 'PHONE' && (
                <small className="text-muted">Digits only, 1 to 14</small>
              )}
              {formData.type === 'EMAIL' && (
                <small className="text-muted">Format: user@domain.tld</small>
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isBusy}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleSave} 
          disabled={isBusy || !isFormComplete()}
        >
          {isBusy ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              {isEditMode ? 'Updating...' : 'Adding...'}
            </>
          ) : (
            <>
              <i className="ni ni-check-bold mr-2"></i>
              {isEditMode ? 'Update' : 'Add'} Contact
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ContactFormModal;

